import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Lock, ArrowLeft, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { items, total } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', postcode: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
        shipping: form,
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      else setError('Could not start checkout. Please try again.');
    } catch (err) {
      setError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  const field = 'w-full bg-transparent border-b border-espresso/20 py-3 text-espresso placeholder-mocha/50 focus:outline-none focus:border-gold transition-colors';

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 pt-28">
        <ShoppingBag className="w-12 h-12 text-gold" />
        <p className="font-serif text-3xl text-espresso">Your bag is empty</p>
        <Link to="/shop" className="px-8 py-3 bg-espresso text-ivory text-xs tracking-[0.18em] uppercase hover:bg-gold transition-colors">Browse the store</Link>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-mocha hover:text-gold transition-colors mb-6"><ArrowLeft className="w-4 h-4" /> Continue shopping</Link>
        <h1 className="font-serif text-4xl md:text-5xl text-espresso mb-10">Checkout</h1>
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Delivery form */}
          <form onSubmit={pay} className="lg:col-span-3 bg-cream p-8">
            <h2 className="text-xs tracking-[0.22em] uppercase text-gold mb-6">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <input required name="name" value={form.name} onChange={onChange} placeholder="Full name" className={field} />
              <input required type="email" name="email" value={form.email} onChange={onChange} placeholder="Email address" className={field} />
              <input required name="phone" value={form.phone} onChange={onChange} placeholder="Phone" className={field} />
              <input required name="postcode" value={form.postcode} onChange={onChange} placeholder="Postcode" className={field} />
            </div>
            <input required name="address" value={form.address} onChange={onChange} placeholder="Street address" className={`${field} mt-6`} />
            <input required name="city" value={form.city} onChange={onChange} placeholder="City / Town" className={`${field} mt-6`} />
            {error && <p className="text-sm text-destructive mt-4">{error}</p>}
            <button type="submit" disabled={loading} className="w-full gold-sheen mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors disabled:opacity-70">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to secure payment…</> : <><Lock className="w-4 h-4" /> Pay with Card · £{total.toFixed(2)}</>}
            </button>
            <p className="text-center text-xs text-mocha mt-4">Secured by Stripe. Card, Apple Pay & Google Pay supported. Tax calculated at checkout.</p>
          </form>

          {/* Order summary */}
          <div className="lg:col-span-2">
            <h2 className="text-xs tracking-[0.22em] uppercase text-gold mb-6">Order Summary</h2>
            <div className="space-y-4">
              {items.map((it) => (
                <div key={it.id} className="flex gap-3 items-center">
                  <div className="w-16 h-16 bg-cream shrink-0 flex items-center justify-center p-1 relative">
                    <img src={it.image} alt={it.name} className="max-w-full max-h-full object-contain" />
                    <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-gold text-espresso text-[0.6rem] rounded-full">{it.qty}</span>
                  </div>
                  <div className="flex-1"><p className="text-[0.6rem] tracking-[0.12em] uppercase text-gold">{it.brand}</p><p className="text-sm text-espresso leading-snug">{it.name}</p></div>
                  <span className="font-serif text-espresso">£{(it.price * it.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-gold-soft mt-6 pt-4 flex items-center justify-between">
              <span className="text-sm tracking-[0.12em] uppercase text-mocha">Subtotal</span>
              <span className="font-serif text-2xl text-espresso">£{total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-mocha mt-2">Delivery & any tax shown at secure payment step.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
